/**
 * M10.2: signed-out screens — sign in, register, check-your-mail, e-mail verification,
 * forgotten password and password reset. Everything works by tap/keyboard (no hover).
 */
import { BrandMark } from './BrandMark.tsx';
import { InstallHint } from './InstallHint.tsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RegisterRequestSchema, ResetPasswordRequestSchema, EmailSchema, passwordStrength, ACCEPT_LEGAL_MESSAGE } from '@galaxis/shared';
import { ApiError } from '../api/client.ts';
import { APP_NAME } from '../brand.ts';
import { ChangelogLink } from './ChangelogPage.tsx';
import { quickFound } from '../api/founding.ts';
import { LegalFooter, LegalConsent, GuestPrivacyLine, CONSENT_VERSIONS } from './LegalLinks.tsx';
import { CookieNotice } from './CookieNotice.tsx';
import { guestLogin, fetchGuestAvailable, login, demoLogin, register, verifyEmail, resendVerification, forgotPassword, resetPassword, fetchRegistrationMode, type RegistrationMode } from '../api/account.ts';

export type AuthLink = { kind: 'verify' | 'reset' | 'invite'; token: string };

type Mode =
  | { m: 'login'; notice?: { ok: boolean; text: string } }
  | { m: 'register'; invite?: string }
  | { m: 'registered'; email: string }
  | { m: 'forgot' }
  | { m: 'verify'; token: string }
  | { m: 'reset'; token: string };

interface LoginScreenProps {
  onLogin: () => void;
  link?: AuthLink | null;
}

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/** The server mails at most one verification link per minute per account; the buttons mirror that. */
const RESEND_COOLDOWN_S = 60;
function useCooldown(initial = 0): [number, () => void] {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return;
    const t = window.setTimeout(() => setLeft(l => l - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(RESEND_COOLDOWN_S)];
}

const STRENGTH_COLORS = ['#475569', '#ef4444', '#f59e0b', '#84cc16', '#22c55e'];
function StrengthMeter({ password }: { password: string }) {
  const { score, label } = passwordStrength(password);
  if (!password) return null;
  return (
    <div data-testid="password-strength" aria-live="polite">
      <div className="flex gap-1" aria-hidden>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= score ? STRENGTH_COLORS[score] : 'rgba(148,163,184,0.2)' }} />
        ))}
      </div>
      <p className="text-xs mt-1" style={{ color: STRENGTH_COLORS[score] }}>Jelszó erőssége: {label}</p>
    </div>
  );
}

/** Verification links are single-use; StrictMode's double effect must not spend one twice. */
const verifyCalls = new Map<string, ReturnType<typeof verifyEmail>>();

function Field(props: {
  id: string; label: string; type?: string; value: string; onChange: (v: string) => void;
  autoComplete?: string; error?: string; inputMode?: 'email' | 'text'; autoFocus?: boolean;
  enterKeyHint?: 'next' | 'go' | 'done' | 'send';
}) {
  return (
    <div>
      <label htmlFor={props.id} className="field-label">{props.label}</label>
      <input
        id={props.id} name={props.id} type={props.type ?? 'text'} value={props.value}
        onChange={e => props.onChange(e.target.value)} autoComplete={props.autoComplete}
        inputMode={props.inputMode} autoFocus={props.autoFocus} enterKeyHint={props.enterKeyHint ?? 'next'}
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        aria-invalid={props.error ? 'true' : undefined}
        aria-describedby={props.error ? `${props.id}-err` : undefined}
        className="field-input"
      />
      {props.error && <p id={`${props.id}-err`} className="field-error" role="alert">{props.error}</p>}
    </div>
  );
}

function Notice({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  return <div className={`notice ${ok ? 'notice-ok' : 'notice-error'}`} role={ok ? 'status' : 'alert'}>{children}</div>;
}

export function LoginScreen({ onLogin, link }: LoginScreenProps) {
  const [mode, setMode] = useState<Mode>(
    link?.kind === 'verify' ? { m: 'verify', token: link.token } : link?.kind === 'reset' ? { m: 'reset', token: link.token }
      : link?.kind === 'invite' ? { m: 'register', invite: link.token } : { m: 'login' },
  );
  const stars = useMemo(() => Array.from({ length: 70 }, () => ({
    s: Math.random() < 0.7 ? 1 : 2, l: Math.random() * 100, t: Math.random() * 100, o: 0.1 + Math.random() * 0.4,
  })), []);
  const isTab = mode.m === 'login' || mode.m === 'register';

  return (
    <div className="w-full h-full overflow-y-auto bg-space-900" style={{ touchAction: 'pan-y' }}>
      <CookieNotice />
      <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {stars.map((st, i) => (
          <div key={i} className="absolute rounded-full bg-white"
            style={{ width: st.s, height: st.s, left: `${st.l}%`, top: `${st.t}%`, opacity: st.o }} />
        ))}
      </div>

      <div className="relative z-10 min-h-full flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3" aria-hidden><BrandMark size={72} /></div>
            <h1 className="font-mono text-3xl sm:text-4xl font-bold tracking-[0.3em] text-accent mb-1 uppercase">{APP_NAME}</h1>
            <p className="text-slate-400 text-sm">Galaktikus stratégia – egyetlen közös univerzum</p>
          </div>

          {isTab && <GuestPlay onLogin={onLogin} />}
          {isTab && <InstallHint className="mb-4" />}

          <div className="sci-fi-panel rounded-xl p-4 sm:p-6 text-left">
            {isTab && (
              <div className="flex gap-1 mb-5 p-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }} role="tablist">
                {(['login', 'register'] as const).map(t => (
                  <button
                    key={t} type="button" role="tab" aria-selected={mode.m === t}
                    onClick={() => setMode(t === 'login' ? { m: 'login' } : { m: 'register' })}
                    className="flex-1 rounded-md text-sm font-medium transition-colors"
                    style={{
                      minHeight: 44,
                      background: mode.m === t ? 'rgba(74,158,255,0.2)' : 'transparent',
                      color: mode.m === t ? '#4a9eff' : '#94a3b8',
                      border: mode.m === t ? '1px solid rgba(74,158,255,0.3)' : '1px solid transparent',
                    }}
                  >
                    {t === 'login' ? 'Bejelentkezés' : 'Regisztráció'}
                  </button>
                ))}
              </div>
            )}

            {mode.m === 'login' && <LoginForm notice={mode.notice} onLogin={onLogin} setMode={setMode} />}
            {mode.m === 'register' && <RegisterForm setMode={setMode} invite={mode.invite} />}
            {mode.m === 'registered' && <Registered email={mode.email} setMode={setMode} />}
            {mode.m === 'forgot' && <ForgotForm setMode={setMode} />}
            {mode.m === 'verify' && <VerifyPanel token={mode.token} setMode={setMode} onLogin={onLogin} />}
            {mode.m === 'reset' && <ResetForm token={mode.token} setMode={setMode} />}
          </div>
          <p className="text-center mt-4"><ChangelogLink /></p>
          <LegalFooter className="mt-2" />
        </div>
      </div>
    </div>
  );
}

type SetMode = (m: Mode) => void;

/** Play at once, register later: a guest session + a quick-founded civilization. */
function GuestPlay({ onLogin }: { onLogin: () => void }) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    fetchGuestAvailable().then(g => { if (live) setAvailable(g); }).catch(() => undefined);
    return () => { live = false; };
  }, []);
  if (!available) return null;

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const auth = await guestLogin();
      // A failed quick start is not fatal: the founding screen lets the guest choose by hand
      await quickFound(auth.player.id).catch(() => null);
      onLogin();
    } catch (err) {
      setError(errText(err, 'A vendégjáték most nem indítható.'));
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 flex flex-col gap-2">
      <button type="button" className="btn-primary" style={{ minHeight: 56, fontSize: '1.05rem' }} disabled={busy} onClick={() => void start()} data-testid="guest-play">
        {busy ? 'Indítás…' : 'Játék regisztráció nélkül'}
      </button>
      <p className="text-center text-xs text-slate-500">Azonnal kezdhetsz; a fiókot később is elmentheted, a civilizációd megmarad.</p>
      <GuestPrivacyLine />
      {error && <Notice>{error}</Notice>}
    </div>
  );
}

function LoginForm({ onLogin, setMode, notice }: { onLogin: () => void; setMode: SetMode; notice?: { ok: boolean; text: string } }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resent, setResent] = useState<string | null>(null);
  const [cooldown, startCooldown] = useCooldown();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { setError('Add meg a felhasználóneved/e-mail-címed és a jelszavad.'); return; }
    setLoading(true); setError(null); setUnverified(false); setResent(null);
    try {
      await login(identifier.trim(), password);
      onLogin();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setUnverified(true);
        if (identifier.includes('@')) setResendEmail(identifier.trim());
      }
      setError(errText(err, 'Sikertelen bejelentkezés.'));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    const parsed = EmailSchema.safeParse(resendEmail);
    if (!parsed.success) { setResent(null); setError(parsed.error.errors[0]?.message ?? 'Érvénytelen e-mail-cím'); return; }
    try { setResent((await resendVerification(parsed.data)).message); setError(null); startCooldown(); } catch (err) { setError(errText(err, 'Nem sikerült.')); }
  };

  const demo = async () => {
    setLoading(true); setError(null);
    try { await demoLogin(); onLogin(); } catch (err) { setError(errText(err, 'Ismeretlen hiba')); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" noValidate aria-label="Bejelentkezés">
      {notice && <Notice ok={notice.ok}>{notice.text}</Notice>}
      <Field id="login-identifier" label="Felhasználónév vagy e-mail" value={identifier} onChange={setIdentifier} autoComplete="username" />
      <Field id="login-password" label="Jelszó" type="password" value={password} onChange={setPassword} autoComplete="current-password" enterKeyHint="go" />
      <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Belépés…' : 'Bejelentkezés'}</button>
      {error && <Notice>{error}</Notice>}
      {unverified && (
        <div className="flex flex-col gap-2" data-testid="resend-verification">
          <Field id="resend-email" label="E-mail-cím az új megerősítő levélhez" type="email" inputMode="email" value={resendEmail} onChange={setResendEmail} autoComplete="email" />
          <button type="button" className="btn-secondary" onClick={resend} disabled={cooldown > 0}>{cooldown > 0 ? `Újraküldhető ${cooldown} mp múlva` : 'Megerősítő levél újraküldése'}</button>
          {resent && <Notice ok>{resent}</Notice>}
        </div>
      )}
      <div className="flex flex-wrap justify-between gap-x-4">
        <button type="button" className="btn-link" onClick={() => setMode({ m: 'forgot' })}>Elfelejtett jelszó</button>
        {import.meta.env.DEV && (
          <button type="button" onClick={demo} disabled={loading} className="btn-link" style={{ color: '#94a3b8' }}>
            Demo bejelentkezés (fejlesztői mód)
          </button>
        )}
      </div>
    </form>
  );
}

function RegisterForm({ setMode, invite }: { setMode: SetMode; invite?: string }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', passwordConfirm: '', inviteCode: invite ?? '' });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // M10.6: the closed Alpha needs an invitation; registration can also be paused
  const [regMode, setRegMode] = useState<RegistrationMode | null>(null);
  useEffect(() => {
    let live = true;
    fetchRegistrationMode().then(m => { if (live) setRegMode(m); }).catch(() => { if (live) setRegMode('open'); });
    return () => { live = false; };
  }, []);
  const set = (k: keyof typeof form) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const { inviteCode, ...rest } = form;
    const code = inviteCode.trim();
    const parsed = RegisterRequestSchema.safeParse({ ...(code ? { ...rest, inviteCode: code } : rest), acceptLegal: true, legalVersions: CONSENT_VERSIONS });
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.errors) { const k = String(i.path[0] ?? 'form'); errs[k] ??= i.message; }
    if (!accepted) errs['acceptLegal'] = ACCEPT_LEGAL_MESSAGE;
    if (!errs['password'] && form.username && form.password.toLowerCase().includes(form.username.trim().toLowerCase())) {
      errs['password'] = 'A jelszó nem tartalmazhatja a felhasználónevet';
    }
    if (regMode === 'invite' && !code) errs['inviteCode'] = 'A zárt Alpha-tesztbe csak meghívóval lehet regisztrálni.';
    else if (errs['inviteCode']) errs['inviteCode'] = 'A meghívókód formátuma hibás — másold be a levélben kapott linkből.';
    setErrors(errs);
    if (Object.keys(errs).length > 0 || !parsed.success) return;
    setLoading(true);
    try {
      await register(parsed.data);
      setMode({ m: 'registered', email: parsed.data.email });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'username_taken') setErrors({ username: err.message });
      else if (err instanceof ApiError && err.code === 'email_not_allowed') setErrors({ email: err.message });
      else if (err instanceof ApiError && err.code === 'invite_invalid') setErrors({ inviteCode: err.message });
      else if (err instanceof ApiError && err.code === 'registration_closed') { setRegMode('closed'); setError(err.message); }
      else if (err instanceof ApiError && err.code === 'legal_outdated') setError(err.message);
      else setError(errText(err, 'Regisztrációs hiba.'));
    } finally {
      setLoading(false);
    }
  };

  if (regMode === null) return <p className="text-sm text-slate-400" role="status">Betöltés…</p>;
  if (regMode === 'closed') {
    return (
      <div className="flex flex-col gap-3" data-testid="registration-closed">
        <Notice>A regisztráció jelenleg szünetel. Ha már van fiókod, nyugodtan jelentkezz be.</Notice>
        <button type="button" className="btn-primary" onClick={() => setMode({ m: 'login' })}>Bejelentkezés</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" noValidate aria-label="Regisztráció">
      {regMode === 'invite' && (
        <>
          <p className="text-sm text-slate-400">Zárt Alpha-teszt: a regisztrációhoz meghívó kell.</p>
          <Field id="reg-invite" label="Meghívókód" value={form.inviteCode} onChange={set('inviteCode')} autoComplete="off" error={errors['inviteCode']} />
        </>
      )}
      <Field id="reg-username" label="Felhasználónév" value={form.username} onChange={set('username')} autoComplete="username" error={errors['username']} />
      <Field id="reg-email" label="E-mail-cím" type="email" inputMode="email" value={form.email} onChange={set('email')} autoComplete="email" error={errors['email']} />
      <Field id="reg-password" label="Jelszó (min. 10 karakter, betű és szám)" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" error={errors['password']} />
      <StrengthMeter password={form.password} />
      <Field id="reg-password2" label="Jelszó megerősítése" type="password" value={form.passwordConfirm} onChange={set('passwordConfirm')} autoComplete="new-password" enterKeyHint="go" error={errors['passwordConfirm']} />
      <LegalConsent id="reg-legal" checked={accepted} onChange={setAccepted} error={errors['acceptLegal']} />
      <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Regisztráció…' : 'Fiók létrehozása'}</button>
      {error && <Notice>{error}</Notice>}
      <p className="text-xs text-slate-500">A belépéshez meg kell erősítened az e-mail-címed. Az első belépéskor alapítod meg a civilizációdat.</p>
    </form>
  );
}

function Registered({ email, setMode }: { email: string; setMode: SetMode }) {
  const [msg, setMsg] = useState<string | null>(null);
  // The sign-up mail has just gone out: the next one may be requested after the server's cooldown
  const [cooldown, startCooldown] = useCooldown(RESEND_COOLDOWN_S);
  return (
    <div className="flex flex-col gap-3" data-testid="registered">
      <h2 className="text-base font-semibold text-slate-100">Nézd meg a postafiókod</h2>
      <Notice ok>Megerősítő levelet küldtünk ide: <strong className="break-all">{email}</strong>. A levélben lévő linkkel aktiválhatod a fiókod.</Notice>
      <p className="text-xs text-slate-400">Nem találod? Nézd meg a spam mappát is; a levél néhány percen belül megérkezik. A link 24 óráig érvényes.</p>
      <button type="button" className="btn-secondary" disabled={cooldown > 0} onClick={async () => {
        try { setMsg((await resendVerification(email)).message); startCooldown(); } catch (e) { setMsg(errText(e, 'Nem sikerült.')); }
      }}>{cooldown > 0 ? `Újraküldhető ${cooldown} mp múlva` : 'Levél újraküldése'}</button>
      {msg && <p className="text-xs text-slate-400" role="status">{msg}</p>}
      <button type="button" className="btn-primary" onClick={() => setMode({ m: 'login' })}>Vissza a bejelentkezéshez</button>
    </div>
  );
}

function ForgotForm({ setMode }: { setMode: SetMode }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = EmailSchema.safeParse(email);
    if (!parsed.success) { setError(parsed.error.errors[0]?.message ?? 'Érvénytelen e-mail-cím'); return; }
    setLoading(true); setError(null);
    try { setDone((await forgotPassword(parsed.data)).message); } catch (err) { setError(errText(err, 'Nem sikerült.')); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" noValidate aria-label="Elfelejtett jelszó">
      <h2 className="text-base font-semibold text-slate-100">Elfelejtett jelszó</h2>
      <p className="text-sm text-slate-400">Add meg a fiókodhoz tartozó e-mail-címet; küldünk egy linket az új jelszó beállításához.</p>
      <Field id="forgot-email" label="E-mail-cím" type="email" inputMode="email" value={email} onChange={setEmail} autoComplete="email" enterKeyHint="send" error={error ?? undefined} />
      <button type="submit" disabled={loading || !!done} className="btn-primary">{loading ? 'Küldés…' : 'Visszaállító link kérése'}</button>
      {done && <Notice ok>{done}</Notice>}
      <button type="button" className="btn-link self-start" onClick={() => setMode({ m: 'login' })}>Vissza a bejelentkezéshez</button>
    </form>
  );
}

function VerifyPanel({ token, setMode, onLogin }: { token: string; setMode: SetMode; onLogin: () => void }) {
  const [state, setState] = useState<{ ok: boolean; text: string; signedIn?: boolean } | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    let call = verifyCalls.get(token);
    if (!call) { call = verifyEmail(token); verifyCalls.set(token, call); }
    call.then(r => setState({ ok: true, text: r.message, signedIn: !!r.session }), e => setState({ ok: false, text: errText(e, 'A megerősítés nem sikerült.') }));
  }, [token]);

  return (
    <div className="flex flex-col gap-3" data-testid="verify-email">
      <h2 className="text-base font-semibold text-slate-100">E-mail-cím megerősítése</h2>
      {!state && <p className="text-sm text-slate-400" role="status">Megerősítés folyamatban…</p>}
      {state && <Notice ok={state.ok}>{state.text}</Notice>}
      {state?.ok && state.signedIn && (
        <>
          <p className="text-sm text-slate-300">A fiókod aktív. Itt az ideje megalapítani a civilizációdat!</p>
          <button type="button" className="btn-primary" style={{ minHeight: 48 }} onClick={onLogin} data-testid="verify-continue">Tovább a játékba</button>
        </>
      )}
      {state?.ok && !state.signedIn && (
        <button type="button" className="btn-primary" onClick={() => setMode({ m: 'login', notice: { ok: true, text: 'Az új e-mail-címed érvényes, jelentkezz be.' } })}>Tovább a bejelentkezéshez</button>
      )}
      {state && !state.ok && <VerifyFailed setMode={setMode} />}
    </div>
  );
}

/** Expired / already used link: say so plainly and offer a fresh one. */
function VerifyFailed({ setMode }: { setMode: SetMode }) {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [cooldown, startCooldown] = useCooldown();
  const send = async () => {
    const parsed = EmailSchema.safeParse(email);
    if (!parsed.success) { setMsg({ ok: false, text: parsed.error.errors[0]?.message ?? 'Érvénytelen e-mail-cím' }); return; }
    try { setMsg({ ok: true, text: (await resendVerification(parsed.data)).message }); startCooldown(); } catch (err) { setMsg({ ok: false, text: errText(err, 'Nem sikerült.') }); }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="verify-failed">
      <p className="text-sm text-slate-300">A megerősítő linkek 24 óráig érvényesek, és csak egyszer használhatók. Ha a fiókod már aktív, egyszerűen jelentkezz be; ha nem, kérj új levelet.</p>
      <Field id="verify-resend-email" label="E-mail-cím az új megerősítő levélhez" type="email" inputMode="email" value={email} onChange={setEmail} autoComplete="email" />
      <button type="button" className="btn-primary" disabled={cooldown > 0} onClick={() => void send()}>{cooldown > 0 ? `Újraküldhető ${cooldown} mp múlva` : 'Új megerősítő levél kérése'}</button>
      {msg && <Notice ok={msg.ok}>{msg.text}</Notice>}
      <button type="button" className="btn-link self-start" onClick={() => setMode({ m: 'login' })}>Tovább a bejelentkezéshez</button>
    </div>
  );
}

function ResetForm({ token, setMode }: { token: string; setMode: SetMode }) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = ResetPasswordRequestSchema.safeParse({ token, password, passwordConfirm });
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.errors) { const k = String(i.path[0] ?? 'form'); errs[k] ??= i.message; }
    setErrors(errs);
    if (!parsed.success) { if (errs['token']) setError('A visszaállító link érvénytelen.'); return; }
    setLoading(true);
    try {
      const r = await resetPassword(token, password, passwordConfirm);
      setMode({ m: 'login', notice: { ok: true, text: r.message } });
    } catch (err) {
      setError(errText(err, 'Nem sikerült beállítani az új jelszót.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" noValidate aria-label="Új jelszó">
      <h2 className="text-base font-semibold text-slate-100">Új jelszó beállítása</h2>
      <Field id="reset-password" label="Új jelszó (min. 10 karakter, betű és szám)" type="password" value={password} onChange={setPassword} autoComplete="new-password" error={errors['password']} />
      <Field id="reset-password2" label="Új jelszó megerősítése" type="password" value={passwordConfirm} onChange={setPasswordConfirm} autoComplete="new-password" enterKeyHint="go" error={errors['passwordConfirm']} />
      <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Mentés…' : 'Új jelszó mentése'}</button>
      {error && <Notice>{error}</Notice>}
      <button type="button" className="btn-link self-start" onClick={() => setMode({ m: 'forgot' })}>Új link kérése</button>
    </form>
  );
}
